import { isDefined } from "@clipboard-health/util-ts";
import { Box, CardActionArea, CardContent, Stack } from "@mui/material";
import {
  Card,
  DotSeparatedList,
  Text,
  TextSkeleton,
} from "@src/appV2/redesign/system/ui-components";

import { QUICK_SIGN_TEXT_WITH_LIGHTING_EMOJI } from "../DocumentsPage/constants";
import { useGetQualificationsForRequirement } from "../useGetQualificationsForRequirement";
import { type ApplicableWorkplacesDetails } from "../utils/getApplicableWorkplacesTextForRequirement";
import { getDocumentMetadata } from "../utils/getDocumentMetadata";
import { DocumentStatusPill, type DocumentStatusPillVariant } from "./StatusPill";

export interface DocumentOverviewCardProps {
  requirementName: string;
  documentStatusPillVariant?: DocumentStatusPillVariant;
  documentStatusPillLabel?: string;
  timeToReviewInMinutes?: number;
  qualifications: string[];
  applicableWorkplacesDetails?: ApplicableWorkplacesDetails;
  expiryDate?: Date;
  isQuickSign?: boolean;
  applicabilityLabel?: string;
  onClick: () => void;
}

export function DocumentOverviewCard(props: DocumentOverviewCardProps) {
  const {
    requirementName,
    documentStatusPillVariant,
    documentStatusPillLabel,
    timeToReviewInMinutes,
    applicableWorkplacesDetails,
    qualifications,
    expiryDate,
    isQuickSign,
    applicabilityLabel,
    onClick,
  } = props;

  const { qualificationDisplayLabels, isLoading } =
    useGetQualificationsForRequirement(qualifications);

  const documentMetadataTextElements = [
    ...getDocumentMetadata({
      applicableWorkplacesDetails,
      timeToReviewInMinutes,
      expiryDate,
      qualifications: qualificationDisplayLabels,
      isQuickSign,
      documentStatusPillVariant,
      maxNumberMetadataItems: 3,
    }),
    ...(isDefined(applicabilityLabel) ? [applicabilityLabel] : []),
  ];

  return (
    <Card sx={(theme) => ({ minHeight: theme.size!.documents.card.minHeight, flexShrink: 0 })}>
      <CardActionArea sx={{ flexGrow: 1 }} onClick={onClick}>
        <CardContent>
          <Stack direction="column" justifyContent="space-between" spacing={7}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={5}>
              <Text semibold variant="h6">
                {requirementName}
              </Text>

              {isDefined(documentStatusPillVariant) && (
                <Box sx={{ alignSelf: "flex-start" }}>
                  <DocumentStatusPill
                    size="small"
                    variant={documentStatusPillVariant}
                    expiry={expiryDate}
                    label={documentStatusPillLabel}
                  />
                </Box>
              )}
            </Stack>
            {isLoading ? (
              <TextSkeleton variant="body2" width="50%" />
            ) : (
              <DotSeparatedList>
                {documentMetadataTextElements.map((text) => {
                  const isQuickSign = text === QUICK_SIGN_TEXT_WITH_LIGHTING_EMOJI;

                  return (
                    <Text
                      key={text}
                      variant="caption"
                      bold={isQuickSign}
                      sx={{
                        color: (theme) =>
                          isQuickSign ? theme.palette.intent?.success.text : undefined,
                      }}
                    >
                      {text}
                    </Text>
                  );
                })}
              </DotSeparatedList>
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
